import {
  AutocompleteInput,
  NumberInput,
  RadioButtonGroupInput,
  ReferenceArrayInput,
  ReferenceInput,
  required,
  SelectArrayInput,
  SelectInput,
  TextInput,
  useDataProvider,
  useNotify,
} from "react-admin";
import SessionExamDates from "../components/SessionExamDates";
import SessionExamRooms from "../components/SessionExamRooms";
import SessionProfessors from "../components/SessionProfessors";
import { Grid } from "@mui/material";
import { administrationRequests } from "api/administrationApi";
import { useFormContext, useWatch } from "react-hook-form";
import { useEffect, useState } from "react";
import { IntendedFor, NotPastExamsStudentsTypes } from "utils/constants";
import StudyGroupField from "server/components/Fields/StudyGroupField";
import { IntendedForChoices, NotPastExamsStudentsTypeChoices } from "utils/choices";
import { studentRequests } from "api/studentApi";

const CreateFormFields = ({ filters, isWinterOrSummerSession, isExtendedSession }) => {
  const { setValue, getValues } = useFormContext();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [sessionDateRange, setSessionDateRange] = useState(null);

  const structuralUnitId = filters.structuralUnitId;
  const academicYearSessionId = filters.academicYearSessionId;
  const disciplineId = useWatch({ name: "disciplineId" });
  const watchedIntendedFor = useWatch({ name: "intendedFor" });
  const intendedFor = isExtendedSession ? IntendedFor.Individual : watchedIntendedFor;
  const type = useWatch({ name: "type" });

  const validateFacultyNumbers = value => {
    const from = Number(getValues("fromFacultyNumber"));
    const to = Number(getValues("toFacultyNumber"));
    if (from > to) {
      return "Началният факултетен номер не може да е по-голям от крайния";
    }
    return undefined;
  };

  useEffect(() => {
    if (academicYearSessionId) {
      const fetchSessionDateRange = async sessionId => {
        try {
          const { data } = await dataProvider.customRequest(
            studentRequests.getAcademicSessionDates(sessionId),
            null,
            "GET",
          );
          setSessionDateRange(data);
        } catch (err) {
          console.error("Неуспешно зареждане на периода", err);
        }
      };
      fetchSessionDateRange(academicYearSessionId);
    }
  }, [academicYearSessionId, dataProvider, notify, setValue]);

  useEffect(() => {
    setValue("academicYearSessionId", academicYearSessionId ?? null);
  }, [academicYearSessionId, setValue]);

  return (
    <>
      <Grid
        container
        spacing={2}
      >
        <Grid
          item
          xs={12}
          md={4}
        >
          <ReferenceInput
            source="disciplineId"
            reference={administrationRequests.getDisciplinesSelect()}
            label="Дисциплина"
          >
            <AutocompleteInput
              label="Дисциплина"
              validate={required()}
              filterToQuery={q => (q?.length > 2 ? { name: q } : {})}
            />
          </ReferenceInput>
        </Grid>
        {disciplineId && (
          <Grid
            item
            xs={12}
            md={4}
          >
            <ReferenceArrayInput
              source="specialityIds"
              reference={studentRequests.getCurriculumsSpecialitiesSelect()}
              filter={{
                disciplineId: disciplineId,
                includeCyclic: true,
                includeElective: true,
                includeFacultative: true,
              }}
              validate={[required()]}
            >
              <SelectArrayInput
                label="Специалност"
                optionText="name"
              />
            </ReferenceArrayInput>
          </Grid>
        )}
      </Grid>

      {disciplineId && (
        <>
          <Grid
            container
            spacing={2}
          >
            <Grid
              item
              xs={12}
              md={3}
            >
              <ReferenceInput
                source="examTypeId"
                reference={administrationRequests.getExamTypesSelect()}
                label="Тип изпит"
              >
                <SelectInput
                  label="Тип изпит"
                  optionText="nameBg"
                  emptyText={""}
                />
              </ReferenceInput>
            </Grid>
            <Grid
              item
              xs={12}
              md={4}
            >
              <TextInput
                source="note"
                label="Бележка"
              />
            </Grid>
            <Grid
              item
              xs={12}
              md={4}
            >
              {isWinterOrSummerSession && (
                <SelectInput
                  source="intendedFor"
                  label="Предназначен за"
                  choices={IntendedForChoices}
                  defaultValue={IntendedForChoices[0].id || undefined}
                />
              )}
            </Grid>
          </Grid>

          <Grid
            container
            spacing={2}
            flex
            flexDirection="column"
          >
            {(!isWinterOrSummerSession || intendedFor === IntendedFor.OnlyNotPastExamsStudents) &&
              intendedFor !== IntendedFor.Individual && (
                <>
                  <Grid
                    item
                    xs={12}
                    md={4}
                  >
                    <RadioButtonGroupInput
                      label="Добави по"
                      source="type"
                      choices={NotPastExamsStudentsTypeChoices}
                      validate={required()}
                      onChange={() => {
                        setValue("fromFacultyNumber", null);
                        setValue("toFacultyNumber", null);
                        setValue("studentsCapacity", null);
                      }}
                    />
                  </Grid>
                  {Number(type) === NotPastExamsStudentsTypes.FacultyNumbers ? (
                    <Grid
                      container
                      flex
                      flexDirection="column"
                      ml={2}
                    >
                      <Grid
                        item
                        xs={12}
                        md={3}
                      >
                        <NumberInput
                          source="fromFacultyNumber"
                          label="от Факултетен номер"
                          validate={[required(), validateFacultyNumbers]}
                        />
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        md={3}
                      >
                        <NumberInput
                          source="toFacultyNumber"
                          label="до Факултетен номер"
                          validate={[required(), validateFacultyNumbers]}
                        />
                      </Grid>
                    </Grid>
                  ) : (
                    <Grid
                      item
                      xs={12}
                      md={3}
                    >
                      <NumberInput
                        source="studentsCapacity"
                        label="Брой студенти"
                        required
                      />
                    </Grid>
                  )}
                </>
              )}
            {intendedFor === IntendedFor.RegularAndNotPastExamsStudents && (
              <Grid
                item
                xs={12}
                md={3}
              >
                <NumberInput
                  source="studentsCapacity"
                  label="Брой студенти"
                  required
                />
              </Grid>
            )}
          </Grid>
        </>
      )}

      <SessionExamRooms />
      <SessionExamDates sessionDateRange={sessionDateRange} />
      {disciplineId && <SessionProfessors disciplineId={disciplineId} />}
      {intendedFor !== IntendedFor.OnlyNotPastExamsStudents && (
        <Grid
          container
          spacing={2}
        >
          {intendedFor !== IntendedFor.Individual && (
            <Grid
              item
              xs={12}
              md={3}
            >
              <StudyGroupField label=" Пример за въвеждане на Учебни групи: 1-5, 8, 11-13, 15. Оставете празно ако изпита се отнася за всички групи!" />
            </Grid>
          )}
          <Grid
            item
            xs={12}
            md={3}
          >
            <ReferenceInput
              source="gradingScaleId"
              reference={administrationRequests.getGradingScalesSelect()}
            >
              <SelectInput
                label="Скала за оценяване"
                optionText="name"
                emptyText={false}
              />
            </ReferenceInput>
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default CreateFormFields;
